/**
 * Shared markdown utilities for Sparx editor and preview.
 * Centralizes variable regex, line-ending normalization, and newline collapse
 * so decoration plugin, sparxMarkdownHtml, MarkdownPreview, and plain-doc conversion stay consistent.
 */

/** Matches {{...}} placeholders. Used for decoration spans and protect/restore in markdown↔HTML. */
export const SPARX_VARIABLE_REGEX = /\{\{[^}]*\}\}/g;

/**
 * Line that starts a list item (unordered -, +, * or ordered 1. 2. …).
 * Used by MarkdownPreview normalization and markdown preview decorations in the editor.
 */
export const MARKDOWN_PREVIEW_LIST_LINE_REGEX = /^[ \t]{0,3}(?:[-+*]|\d+\.)\s/;

/** Normalize CRLF or CR to LF so paragraph splits and display are consistent. */
export const normalizeLineEndings = (s: string): string =>
  s.replace(/\r\n?/g, "\n");

/**
 * Strip leading/trailing newline runs only (after normalizing line endings).
 * Preserves horizontal whitespace on content lines (advisor blocks, indented continuations).
 */
export const trimMarkdownOuterNewlines = (md: string): string =>
  normalizeLineEndings(md).replace(/^\n+/, "").replace(/\n+$/, "");

/** True when the string has no non-whitespace characters (after trimming outer newlines only). */
export const isMarkdownVisuallyEmpty = (md: string): boolean =>
  !/\S/.test(trimMarkdownOuterNewlines(md));

/** CommonMark hard break inside a paragraph (two spaces + newline); serializes `<br>` / TipTap hardBreak. */
export const MARKDOWN_HARD_BREAK_LINE_ENDING = "  \n";

/** Collapse 3+ newlines to a single blank line. */
const THREE_OR_MORE_NEWLINES = /\n{3,}/g;

export const collapseNewlines = (s: string): string =>
  s.replace(THREE_OR_MORE_NEWLINES, "\n\n");
